import { useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { NuqsAdapter } from "nuqs/adapters/react";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppRoutes } from "@/routes";
import { RoleProvider } from "@/contexts/RoleContext";
import { BrokerTourProvider } from "@/components/tours/BrokerTourManager";
import { BrokerTour } from "@/components/tours/BrokerTour";
import { EmployerTourProvider } from "@/components/tours/EmployerTourManager";
import { EmployerTour } from "@/components/tours/EmployerTour";
import { useAppointmentReminders } from "@/hooks/useAppointmentReminders";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { StagingWatermark } from "@/components/StagingWatermark";
import { AppUpdateWatcher } from "@/components/AppUpdateWatcher";

function AppointmentReminders() {
  useAppointmentReminders();
  return null;
}

// Public share pages are documents sent to a CLIENT but render inside this same
// SPA, so without this they inherit the BROKER's dark-mode preference — and the
// RFP comparison table is only half dark-styled, so dark renders it unreadable.
// Keep in agreement with the token routes in client/src/routes/publicRoutes.tsx.
const PUBLIC_LIGHT_ROUTE = /^\/(proposal|proposals|quote)\//;

function AppErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          An unexpected error occurred. You can try going back to the dashboard or refreshing.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => {
              queryClient.clear();
              window.location.href = "/";
            }}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [location] = useLocation();
  const forcedTheme = PUBLIC_LIGHT_ROUTE.test(location) ? "light" : undefined;
  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem forcedTheme={forcedTheme}>
        <StagingWatermark />
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <RoleProvider>
              <BrokerTourProvider>
                <EmployerTourProvider>
                  <TooltipProvider>
                    <Toaster />
                    <AppUpdateWatcher />
                    <AppointmentReminders />
                    <AppRoutes />
                    <BrokerTour />
                    <EmployerTour />
                  </TooltipProvider>
                </EmployerTourProvider>
              </BrokerTourProvider>
            </RoleProvider>
          </QueryClientProvider>
        </NuqsAdapter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
